import { FoodsFormBaseProps, FormBaseContextProvider, useFormBaseContext } from "@foodpilot/foods";
import { Loading } from "src/components/utils/Loading";
import { Box, Divider, Stack, Typography, useTheme } from "@mui/material";
import { useQuestionnaireFormContext } from "src/components/QuestionnaireForm/context/useQuestionnaireForm";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { QuestionnaireUpdatePage } from "./QuestionnaireUpdateFormWithPages";
import { TopBarFullWidth } from "../FormHeader";
import { Fragment } from "react/jsx-runtime";
import { PageHeader } from "../FormHeader/PageHeader";

type QuestionnaireUpdateFormBaseProps = Partial<
    Omit<FoodsFormBaseProps, "pages"> & {
        pages: QuestionnaireUpdatePage[];
    }
>;

export const QuestionnaireUpdateFormBase = (props: QuestionnaireUpdateFormBaseProps) => {
    const { pages, confirmExit, ...formBaseProps } = props;
    const {
        ssqVersion,
        title,
        subtitle,
        backUrl,
        getHeadingUrl,
        appearance = "legacy",
    } = useQuestionnaireFormContext();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const ssqTitle = typeof title === "function" ? title(ssqVersion?.ssq) : title;
    const ssqSubtitle = typeof subtitle === "function" ? subtitle(ssqVersion?.ssq) : subtitle;

    return (
        <Box>
            <FormBaseContextProvider
                openConfirmDialog={() => {
                    return;
                }}
                textOptions={{
                    title: ssqTitle,
                    subtitle: ssqSubtitle ?? t("word.loading_wait"),
                    nextPage: t("next"),
                    previousPage: t("previous"),
                    finalPage: t("complete"),
                    syncingMessage: t("enregistrement_en_cours") + "...",
                    savingDefaultMessage: t("informations_enregistrees_il_y_a_quelques_secondes"),
                }}
                pages={pages ?? [{ content: <Loading appearance={appearance} /> }]}
                onPageChange={(newPageIndex: number, previousPageIndex: number) => {
                    if (!newPageIndex && !previousPageIndex) {
                        navigate(backUrl);
                        return;
                    }

                    const headingPageId = pages?.[newPageIndex].id;
                    if (headingPageId === undefined) return;

                    navigate(getHeadingUrl(headingPageId));
                }}
                onValidate={() => navigate(backUrl)}
                onClose={() => navigate(backUrl)}
                confirmExit={false}
                showSync={true}
                {...formBaseProps}
            >
                <TopBarFullWidth pages={pages} />
                <CurrentQuestionnairePage />
            </FormBaseContextProvider>
        </Box>
    );
};

const CurrentQuestionnairePage = () => {
    const { selectedPage, selectedPageIndex } = useFormBaseContext();
    const theme = useTheme();

    const { title, subtitle, content, icon } = selectedPage;

    return (
        <Stack
            alignItems="center"
            padding={{
                xs: theme.spacing(2),
                md: theme.spacing(4),
            }}
        >
            <Stack
                width="100%"
                maxWidth={{ xs: "600px", md: "1200px", xl: "1500px" }}
                gap={{ xs: theme.spacing(2), md: theme.spacing(4) }}
            >
                <Stack alignItems={"start"}>
                    {title && <PageHeader title={title} badge={icon ?? selectedPageIndex + 1} />}
                    {subtitle && (
                        <Fragment>
                            <Typography variant="h4">{subtitle}</Typography>
                            <Divider />
                        </Fragment>
                    )}
                </Stack>
                <Stack>{content}</Stack>
            </Stack>
        </Stack>
    );
};
